import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { AudioWaveform, ChevronDown, PanelLeft, Plus, Coins, X } from "lucide-react";
import { cn } from "../../lib/utils";
import type { WalletState } from "../../hooks/use-wallet";
import { usePersistentState } from "../../hooks/use-persistent-state";
import type { SidebarItem } from "./sidebar-config";
import { useNavStore } from "./nav-store";

interface SidebarProps {
  homePath: string;
  initialOpenGroups?: string[];
  navItems?: SidebarItem[];
  navSections?: import("./sidebar-config").SidebarSection[];
  sectionLabel?: string;
  wallet?: WalletState;
}

function formatCredits(value: number): string {
  return Math.round(value).toLocaleString("en-IN");
}

function routeMatches(pathname: string, href: string) {
  const cleanHref = href.split("?")[0];
  if (cleanHref === "/") return pathname === "/";
  return pathname === cleanHref || pathname.startsWith(`${cleanHref}/`);
}

function getActiveChildHref(pathname: string, item: SidebarItem) {
  return item.children
    ?.filter((child) => routeMatches(pathname, child.href))
    .sort((a, b) => b.href.length - a.href.length)[0]?.href;
}

export function Sidebar({
  homePath,
  initialOpenGroups = [],
  navItems,
  navSections,
  sectionLabel,
  wallet,
}: SidebarProps) {
  const { pathname } = useLocation();
  const [collapsed, setCollapsed] = usePersistentState("sidebar:collapsed", false);
  const [openGroups, setOpenGroups] = useState<string[]>(initialOpenGroups);
  const { mobileOpen, closeMobileNav } = useNavStore();

  // Auto-collapse mobile drawer whenever route changes
  useEffect(() => {
    closeMobileNav();
  }, [pathname, closeMobileNav]);

  // Handle Escape key to close mobile drawer
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && mobileOpen) {
        closeMobileNav();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [mobileOpen, closeMobileNav]);

  // Prevent background body scroll when mobile drawer is open (with cleanup)
  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [mobileOpen]);

  useEffect(() => {
    const itemsToCheck = navSections ? navSections.flatMap(s => s.items) : (navItems || []);
    itemsToCheck.forEach((item) => {
      if (getActiveChildHref(pathname, item)) {
        setOpenGroups((groups) => (
          groups.includes(item.label) ? groups : [...groups, item.label]
        ));
      }
    });
  }, [navItems, navSections, pathname]);

  const toggleGroup = (label: string) => {
    if (collapsed && !mobileOpen) {
      setCollapsed(false);
      setOpenGroups((groups) => (
        groups.includes(label) ? groups : [...groups, label]
      ));
      return;
    }

    setOpenGroups((groups) => (
      groups.includes(label)
        ? groups.filter((group) => group !== label)
        : [...groups, label]
    ));
  };

  const isExpanded = !collapsed || mobileOpen;

  return (
    <>
      {/* ── Mobile Backdrop ─────────────────────────── */}
      {mobileOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 backdrop-blur-sm transition-opacity md:hidden"
          onClick={closeMobileNav}
          aria-hidden="true"
        />
      )}

      {/* ── Sidebar Shell ───────────────────────────── */}
      <aside
        id="mobile-nav"
        role={mobileOpen ? "dialog" : undefined}
        aria-modal={mobileOpen ? "true" : undefined}
        aria-label={sectionLabel}
        className={cn(
          "flex h-screen flex-col border-r border-line bg-[var(--sidebar-bg)] shadow-sm transition-all duration-300",
          // Desktop: relative in flow, collapsible width
          "hidden md:flex md:relative",
          collapsed ? "md:w-[68px]" : "md:w-64",
          // Mobile: slide-in fixed drawer when mobileOpen is true
          mobileOpen && "fixed inset-y-0 left-0 z-50 flex w-72 max-w-[85vw] shadow-2xl"
        )}
      >
        {/* ── Logo & Mobile Close ───────────────────────── */}
        <div className="flex h-16 items-center justify-between gap-2 border-b border-line/50 px-4">
          <div className={cn("relative", collapsed && !mobileOpen ? "hidden" : "flex-1")}>
            <Link to={homePath} onClick={closeMobileNav} className="flex py-2 text-lg font-bold tracking-tight text-ink">
              TechLadder<span className="text-ink-3">.ai</span>
            </Link>
          </div>

          {/* Mobile Close Button */}
          <button
            type="button"
            onClick={closeMobileNav}
            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg text-ink-3 transition-colors hover:bg-soft hover:text-ink md:hidden"
            aria-label="Close navigation menu"
          >
            <X className="h-5 w-5" />
          </button>

          {/* Desktop-only collapse toggle */}
          <button
            type="button"
            onClick={() => setCollapsed((c) => !c)}
            aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
            title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
            className="order-first hidden h-9 w-9 shrink-0 items-center justify-center rounded-lg text-ink-3 transition-colors hover:bg-soft hover:text-ink md:flex"
          >
            <PanelLeft className="h-5 w-5" />
          </button>
        </div>

        {/* ── Nav ──────────────────────────────────────── */}
        <nav className="flex-1 overflow-y-auto px-3 py-5">
          {(navSections || (navItems ? [{ label: sectionLabel, items: navItems }] : [])).map((section, idx, arr) => (
            <div key={section.label || idx} className="flex flex-col">
              {isExpanded && section.label && (
                <h3 className="mb-2.5 px-3 text-[10px] font-semibold uppercase tracking-[0.12em] text-ink-3">
                  {section.label}
                </h3>
              )}
              <ul className="space-y-1">
                {section.items.map((item) => {
                  const activeChildHref = getActiveChildHref(pathname, item);
                  const isGroupActive = Boolean(activeChildHref);
                  const isLinkActive = item.href ? routeMatches(pathname, item.href) : false;
                  const isOpen = openGroups.includes(item.label);
                  const Icon = item.icon;

                  return (
                    <li key={item.label}>
                      {item.children ? (
                        <>
                          <button
                            onClick={() => toggleGroup(item.label)}
                            aria-expanded={isOpen}
                            title={collapsed && !mobileOpen ? item.label : undefined}
                            className={cn(
                              "flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-[13px] transition-all duration-150",
                              isGroupActive
                                ? "bg-accent-50 font-semibold text-accent-700 dark:bg-accent-500/10 dark:text-accent-400"
                                : "text-ink-muted hover:bg-soft hover:text-ink",
                              collapsed && !mobileOpen && "justify-center px-0",
                            )}
                          >
                            <Icon className={cn("h-[18px] w-[18px] shrink-0", isGroupActive ? "text-accent-700 dark:text-accent-400" : undefined)} />
                            {isExpanded && (
                              <>
                                <span className="flex-1 text-left">{item.label}</span>
                                <ChevronDown
                                  className={cn("h-4 w-4 text-ink-faint transition-transform duration-200", isOpen && "rotate-180")}
                                />
                              </>
                            )}
                          </button>

                          {isExpanded && isOpen && (
                            <ul className="mt-1 ml-[18px] space-y-0.5 border-l-2 border-line pl-3">
                              {item.children.map((child) => {
                                const active = child.href === activeChildHref;

                                return (
                                  <li key={child.href}>
                                    <Link
                                      to={child.href}
                                      onClick={closeMobileNav}
                                      aria-current={active ? "page" : undefined}
                                      className={cn(
                                        "flex items-center justify-between rounded-lg px-3 py-2 text-[13px] transition-colors",
                                        active
                                          ? "bg-paper-card dark:bg-[#292524] font-semibold text-accent-700 dark:text-accent-400 shadow-card dark:shadow-card-dark"
                                          : "text-ink-faint hover:bg-soft hover:text-ink",
                                      )}
                                    >
                                      {child.label}
                                      {typeof child.badge === "number" && child.badge > 0 && (
                                        <span className="rounded-full bg-accent-100 dark:bg-accent-500/10 px-1.5 py-0.5 font-mono text-[10px] font-bold text-accent-700 dark:text-accent-400">
                                          {child.badge}
                                        </span>
                                      )}
                                    </Link>
                                  </li>
                                );
                              })}
                            </ul>
                          )}
                        </>
                      ) : (
                        <Link
                          to={item.href!}
                          onClick={closeMobileNav}
                          aria-current={isLinkActive ? "page" : undefined}
                          title={collapsed && !mobileOpen ? item.label : undefined}
                          className={cn(
                            "flex items-center gap-3 rounded-lg px-3 py-2.5 text-[13px] transition-all duration-150",
                            isLinkActive
                              ? "bg-accent-50 font-semibold text-accent-700 dark:bg-accent-500/10 dark:text-accent-400"
                              : "text-ink-muted hover:bg-soft hover:text-ink",
                            collapsed && !mobileOpen && "justify-center px-0",
                          )}
                        >
                          <Icon className={cn("h-[18px] w-[18px] shrink-0", isLinkActive ? "text-accent-700 dark:text-accent-400" : undefined)} />
                          {isExpanded && <span>{item.label}</span>}
                        </Link>
                      )}
                    </li>
                  );
                })}
              </ul>
              {/* Section divider */}
              {idx < arr.length - 1 && (
                <div className={cn("my-5 mx-auto border-t border-line/50 transition-all duration-300", isExpanded ? "w-3/4" : "w-8")} />
              )}
            </div>
          ))}
        </nav>

        {/* ── Wallet Module ────────────────────────────── */}
        <div className="border-t border-line/50 px-3 py-3">
          {wallet && (
            collapsed && !mobileOpen ? (
              <button
                title={[wallet.promotional, wallet.topup].filter((b) => b.started).map((b) => `${b.type}: ${formatCredits(b.remaining)}`).join(" · ")}
                onClick={() => setCollapsed(false)}
                className="flex h-11 w-full items-center justify-center rounded-xl border border-line bg-elevated text-ink transition-colors hover:bg-soft"
              >
                <Coins className="h-4 w-4" />
              </button>
            ) : (
              <div className="rounded-xl border border-line bg-elevated p-4 text-ink shadow-sm">
                <p className="mb-3 text-xs font-semibold text-ink">Credit balance</p>
                {([wallet.promotional, wallet.topup] as const).filter((block) => block.started).map((block, i) => (
                  <div key={block.type} className={i > 0 ? "mt-2.5" : undefined}>
                    <div className="flex items-baseline justify-between">
                      <p className="font-mono text-[10px] uppercase tracking-wider text-ink-2">{block.type}</p>
                      <p className="font-mono text-[10px] text-ink-2">
                        {formatCredits(block.consumed)} / {formatCredits(block.balance)}
                      </p>
                    </div>
                    <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-line">
                      <div
                        className={cn("h-full rounded-full shadow-sm", block.type === "Promotional" ? "bg-danger" : "bg-accent-500")}
                        style={{ width: `${Math.min(100, block.usedPercent)}%` }}
                      />
                    </div>
                    <p className={cn("mt-1 text-[10px]", block.type === "Promotional" ? "text-danger" : "text-accent-700 dark:text-accent-400")}>
                      {formatCredits(block.remaining)} remaining
                    </p>
                  </div>
                ))}
                <button disabled className="mt-3 flex w-full items-center justify-center gap-1.5 rounded-lg bg-soft py-2 text-xs font-semibold text-ink transition-colors hover:bg-line disabled:cursor-not-allowed disabled:opacity-50">
                  <Plus className="h-3.5 w-3.5" /> Add Credits
                </button>
              </div>
            )
          )}
        </div>
      </aside>
    </>
  );
}

